import { isAppUpdateDirty } from "@/domains/shared/appUpdatePrompt/isAppUpdateDirty";
import { useAppUpdatePromptStore } from "@/domains/shared/appUpdatePrompt/appUpdatePromptStore";

/**
 * Deploy-related lazy chunk / dynamic import failure → immediate hard path (FR-02, BR-05).
 * Ignores Later and dirty deferral; shows unsaved-loss warning when dirty.
 */
function enterHardPathFromChunkFail(): void {
  const state = useAppUpdatePromptStore.getState();
  if (state.hardActive) {
    return;
  }

  state.enterHardPath({
    unsavedLoss: isAppUpdateDirty(),
  });
}

export function installChunkFailHardPath(): () => void {
  const onPreloadError = (event: Event) => {
    event.preventDefault();
    enterHardPathFromChunkFail();
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (!isDeployChunkFailure(event.reason)) {
      return;
    }

    enterHardPathFromChunkFail();
  };

  window.addEventListener("vite:preloadError", onPreloadError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}

export function isDeployChunkFailure(reason: unknown): boolean {
  if (!reason) {
    return false;
  }

  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
        ? reason.message
        : typeof reason === "object" && reason !== null && "message" in reason
          ? String((reason as { message: unknown }).message)
          : "";

  if (!message) {
    return false;
  }

  // Vite / browser messages when a hashed chunk is missing after deploy.
  // WebKit (Mobile Safari / Chrome iOS) can resolve import() to undefined;
  // React.lazy then throws at payload._result.default instead of rejecting.
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk [\w.-]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /_result\.default/i.test(message)
  );
}
